import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

/**
 * "Capabilities available at this facility" — a searchable multi-select scoped to the capabilities
 * a CDMO already claims on its own profile.
 *
 * Deliberately offers **only** the CDMO's stored detailed capabilities: a facility cannot introduce
 * a capability the company has not claimed, and nothing selected here changes the profile-level
 * list. Assigning a capability to one facility never removes it from another, so the same value can
 * appear at several sites.
 *
 * Purely informational — facility-level capabilities are not a matching or scoring input.
 */

export interface FacilityCapabilitySelectProps {
  /** The CDMO's own detailed capabilities, keyed by modality. The only values on offer. */
  profileCapabilities: Record<string, string[]>;
  /** Capabilities currently assigned to this facility. */
  value: string[];
  /** Called with the next assignment list. */
  onChange: (next: string[]) => void;
  /** Read-only rendering for a locked profile. */
  disabled?: boolean;
  /** Labels the control for assistive technology. */
  ariaLabel: string;
}

/** Heading for capabilities assigned here that the profile no longer claims. */
const ORPHANED_GROUP_LABEL = 'No longer in your profile';

/**
 * Modality → the capabilities under it that this CDMO actually claims. Empty modalities dropped.
 *
 * Anything already assigned to this facility but absent from the profile is kept in a trailing
 * group. That only happens with data saved before the profile changed, but without it the value has
 * no row to untick and is not counted in the total, so the panel could read "3 of 2 selected".
 */
function usableModalities(profileCapabilities: Record<string, string[]>, assigned: string[]) {
  const groups = Object.entries(profileCapabilities ?? {})
    .map(([modality, options]) => ({
      modality,
      options: Array.isArray(options) ? options.filter(Boolean) : [],
    }))
    .filter((entry) => entry.options.length > 0);

  const offered = new Set(groups.flatMap((entry) => entry.options));
  const orphaned = assigned.filter((capability) => !offered.has(capability));
  if (orphaned.length > 0) groups.push({ modality: ORPHANED_GROUP_LABEL, options: orphaned });

  return groups;
}

const FacilityCapabilitySelect: React.FC<FacilityCapabilitySelectProps> = ({
  profileCapabilities,
  value,
  onChange,
  disabled = false,
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const panelId = useId();

  const modalities = useMemo(
    () => usableModalities(profileCapabilities, value),
    [profileCapabilities, value]
  );
  const totalCount = useMemo(
    () => modalities.reduce((total, entry) => total + entry.options.length, 0),
    [modalities]
  );

  const needle = query.trim().toLowerCase();
  const visibleModalities = useMemo(
    () =>
      modalities
        .map((entry) => ({
          modality: entry.modality,
          options: needle
            ? entry.options.filter((option) => option.toLowerCase().includes(needle))
            : entry.options,
        }))
        .filter((entry) => entry.options.length > 0),
    [modalities, needle]
  );

  // Close on outside click so the panel behaves like a normal dropdown inside a long form.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  // Opening the control puts the cursor straight in the one search field, so typing works
  // immediately without a second click.
  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  // Reset the filter when the panel closes, so reopening never shows a stale, narrowed list.
  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const toggle = (capability: string) => {
    if (disabled) return;
    onChange(
      value.includes(capability)
        ? value.filter((entry) => entry !== capability)
        : [...value, capability]
    );
  };

  if (totalCount === 0) {
    return (
      <p className="text-sm text-slate-gray border border-dashed border-slate-gray/30 rounded-xl px-4 py-3">
        Add capabilities in the Primary Capabilities step to assign them to this facility.
      </p>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`border rounded-xl bg-white transition-colors ${
          isOpen ? 'border-electric-blue' : 'border-slate-gray/30'
        } ${disabled ? 'opacity-80' : ''}`}
      >
        <button
          type="button"
          onClick={() => !disabled && setIsOpen((open) => !open)}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-controls={panelId}
          aria-label={ariaLabel}
          className={`w-full flex items-center gap-2 px-3 py-2 min-h-[44px] text-left ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <span className="flex-1 min-w-0 text-sm">
            {value.length > 0 ? (
              <span className="text-charcoal">{value.length} selected</span>
            ) : (
              // Deliberately not a search affordance: the single search input lives in the panel
              // below. Showing one here too would read as two search fields, only one of which works.
              !isOpen && <span className="text-slate-gray/70">Select capabilities...</span>
            )}
          </span>
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-slate-gray transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        {/* Chips sit outside the trigger, not inside it: each carries a remove button, and the HTML
            spec forbids interactive content as a descendant of <button> — browsers may reparent it,
            screen readers announce a button inside a button, and every chip added a tab stop within
            the one control. */}
        {value.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2.5">
            {value.map((capability) => (
              <span
                key={capability}
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-md bg-selected-blue/10 border border-selected-blue/25 text-selected-blue text-xs font-medium"
              >
                {capability}
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${capability}`}
                    onClick={() => toggle(capability)}
                    className="opacity-70 hover:opacity-100 cursor-pointer"
                  >
                    <X className="w-3 h-3" aria-hidden="true" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {isOpen && (
          <div id={panelId} className="border-t border-slate-gray/15">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-gray/10">
              <Search className="w-4 h-4 shrink-0 text-slate-gray" aria-hidden="true" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search capabilities..."
                aria-label={`Search capabilities for ${ariaLabel}`}
                className="w-full text-sm text-charcoal bg-transparent border-0 outline-none placeholder:text-slate-gray/70"
              />
            </div>

            <p className="px-3 py-1.5 text-xs text-slate-gray border-b border-slate-gray/10">
              {value.length} of {totalCount} selected
            </p>

            {visibleModalities.length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-gray text-center">
                No capabilities in your profile match &ldquo;{query.trim()}&rdquo;.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 max-h-64 overflow-y-auto">
                {visibleModalities.map((entry) => (
                  <div key={entry.modality} className="p-3 md:border-r md:last:border-r-0 border-slate-gray/10">
                    <p className="text-[11px] font-bold tracking-wide uppercase text-slate-gray mb-2">
                      {entry.modality}
                    </p>
                    <div className="space-y-0.5">
                      {entry.options.map((capability) => {
                        const isSelected = value.includes(capability);
                        return (
                          <button
                            key={capability}
                            type="button"
                            onClick={() => toggle(capability)}
                            disabled={disabled}
                            aria-pressed={isSelected}
                            className={`w-full flex items-start gap-2.5 px-2 py-1.5 rounded-md text-left text-sm transition-colors ${
                              isSelected
                                ? 'bg-selected-blue/10 text-selected-blue font-medium'
                                : 'text-charcoal hover:bg-electric-blue/5'
                            } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span
                              className={`w-4 h-4 mt-0.5 shrink-0 rounded border flex items-center justify-center ${
                                isSelected
                                  ? 'bg-selected-blue border-selected-blue'
                                  : 'bg-white border-slate-gray/50'
                              }`}
                              aria-hidden="true"
                            >
                              {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                            </span>
                            <span className="min-w-0">{capability}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilityCapabilitySelect;
